// "My view" page main pane — ticket dashboard with two-column layout,
// toggleable queue, activity heatmap, and daily update prompt.

import { isActiveColumn, DONE_COLUMN_KEY } from '../../functions/tickets/ticketUtils.js'

const _firstName = (el, root) => {
  const me = el.call('findPerson', root.currentUserEmail)
  return (me?.name || '').split(' ')[0] || 'there'
}

const _me = (root) => root?.currentUserEmail

// Awaiting your approval: state='awaiting_approval' OR labels contains 'ASK-USER'
const _awaitingTickets = (root) =>
  (root.myTickets || []).filter(t =>
    t.state === 'awaiting_approval' ||
    (Array.isArray(t.labels) && t.labels.includes('ASK-USER'))
  )

// Working on: assigned to me + in an active column
const _workingTickets = (root) =>
  (root.myTickets || []).filter(t =>
    isActiveColumn(t.column_key) && t.assignee_email === _me(root)
  )

// Requested: assigned to me by someone else, not yet active (backlog/todo)
const _requestedTickets = (root) =>
  (root.myTickets || []).filter(t =>
    !isActiveColumn(t.column_key) &&
    t.column_key !== DONE_COLUMN_KEY &&
    t.reporter_email &&
    t.reporter_email !== _me(root)
  )

// Backlog: everything else not active and not done
const _backlogTickets = (root) =>
  (root.myTickets || []).filter(t =>
    !isActiveColumn(t.column_key) &&
    t.column_key !== DONE_COLUMN_KEY
  )

// Relative age string — e.g. "3h ago", "2d ago", "just now"
const _relativeAge = (dateStr) => {
  if (!dateStr) return ''
  const diff = Math.max(0, Date.now() - new Date(dateStr).getTime())
  const secs = Math.floor(diff / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Priority color token — mirrors the card helper but used in list rows
const _rowPriorityColor = (p) =>
  ({ critical: 'red', P0: 'red', urgent: 'red', high: 'orange', P1: 'orange', medium: 'blue', P2: 'blue', low: 'caption', P3: 'caption' })[p] || 'caption'

export const TicketsMyViewMain = {
  extends: 'AppMain',
  padding: '0',

  // Inject ASK-USER pulse keyframes once on mount.
  // Two named keyframes are registered so DOMQL's @media CSS prop pattern can
  // select the appropriate one: full opacity+scale pulse for users with no
  // motion preference, opacity-only fade for prefers-reduced-motion: reduce.
  onInit: (el) => {
    if (typeof document === 'undefined') return
    if (!document.querySelector('style[data-ask-user-kf]')) {
      const st = document.createElement('style')
      st.setAttribute('data-ask-user-kf', '')
      st.textContent = [
        '@keyframes askUserPulse {',
        '  0%, 100% { opacity: 1; transform: scale(1); }',
        '  50% { opacity: 0.35; transform: scale(0.97); }',
        '}',
        '@keyframes askUserPulseFade {',
        '  0%, 100% { opacity: 1; }',
        '  50% { opacity: 0.35; }',
        '}'
      ].join('\n')
      document.head.appendChild(st)
    }
  },

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'YOUR WORK · TODAY',
      headline: (el, s) => `Hi ${_firstName(el, s.root)}.`,
      lede: (el, s) => {
        const n = (s.root.myTickets || []).length
        if (!n) return 'No open tickets assigned to you. Nice!'
        return `You have ${n} ticket${n === 1 ? '' : 's'} on your plate.`
      }
    },

    Sections: {
      flow: 'y',
      gap: 'C',
      padding: 'C B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      // ── Awaiting your approval (ASK-USER / awaiting_approval) ──
      // Always rendered so the empty-state confirmation is visible.
      // Border dims to a subtle line when nothing is pending.
      AwaitingApproval: {
        flow: 'y',
        gap: 'Z',
        round: 'A',
        padding: 'B',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: (el, s) => _awaitingTickets(s.root).length ? 'yellow' : 'line',
        background: (el, s) => _awaitingTickets(s.root).length ? 'yellow.06' : 'transparent',

        Header: {
          flow: 'x',
          align: 'center space-between',
          gap: 'Z',

          Left: {
            flow: 'x',
            align: 'center left',
            gap: 'Z',

            // "ASK USER" chip — visible only when decisions are pending
            Chip: {
              tag: 'span',
              hide: (el, s) => !_awaitingTickets(s.root).length,
              fontSize: 'Y2',
              fontWeight: '700',
              padding: 'V Z',
              round: 'C',
              letterSpacing: '.04em',
              background: 'yellow.15',
              color: 'yellow',
              text: 'ASK USER'
            },

            Title: {
              fontSize: 'A',
              fontWeight: '700',
              color: (el, s) => _awaitingTickets(s.root).length ? 'title' : 'caption',
              text: 'Awaiting your approval'
            }
          },

          Count: {
            tag: 'span',
            hide: (el, s) => !_awaitingTickets(s.root).length,
            fontSize: 'Y2',
            color: 'yellow',
            fontWeight: '600',
            text: (el, s) => {
              const n = _awaitingTickets(s.root).length
              return `${n} pending`
            }
          }
        },

        // Row list — one per pending decision (hidden when empty)
        Rows: {
          flow: 'y',
          gap: 'V',
          hide: (el, s) => !_awaitingTickets(s.root).length,
          childExtends: 'AwaitingApprovalRow',
          children: (el, s) => _awaitingTickets(s.root),
          childrenAs: 'state'
        },

        // Empty state — always rendered but hidden when rows exist
        Empty: {
          hide: (el, s) => !!_awaitingTickets(s.root).length,
          padding: 'Z 0',
          color: 'caption',
          fontSize: 'Z2',
          text: 'No decisions waiting on you'
        }
      },

      // ── Working on + Requested sections (full width, stacked) ──
      TopRow: {
        flow: 'y',
        gap: 'C',

        Working: {
          extends: 'IconSection',
          icon: 'animations',
          intro: (s) => {
            const n = _workingTickets(s.root).length
            return [
              { text: 'Currently ', color: 'caption' },
              { text: 'working on', color: 'cyan', weight: '700' },
              { text: ` · ${n} ticket${n === 1 ? '' : 's'}`, color: 'caption' }
            ]
          },
          // Drop target — accept dragged tickets from queue
          onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' },
          onDragEnter: (e, el) => {
            if (!el.node) return
            el.node.style.outline = '2px solid var(--color-blue)'
            el.node.style.background = 'rgba(0, 133, 255, 0.06)'
            el.node.style.borderRadius = 'var(--spacing-A)'
          },
          onDragLeave: (e, el) => {
            if (!el.node) return
            // Only clear if leaving the container entirely (not entering a child)
            if (el.node.contains(e.relatedTarget)) return
            el.node.style.outline = ''
            el.node.style.background = ''
            el.node.style.borderRadius = ''
          },
          onDrop: (e, el, s) => {
            e.preventDefault()
            if (el.node) { el.node.style.outline = ''; el.node.style.background = ''; el.node.style.borderRadius = '' }
            const ticketId = parseInt(e.dataTransfer.getData('text/plain'), 10)
            if (!ticketId) return
            el.call('moveTicket', { ticketId, columnKey: '4d6ab783', position: 0 })
            const root = s.root
            root.update({
              _dragTicketId: null,
              myTickets: (root.myTickets || []).map(t =>
                t.id === ticketId ? { ...t, column_key: '4d6ab783' } : t
              )
            })
          },
          Right: {
            Body: {
              extends: 'TicketCardGrid',
              children: (el, s) => _workingTickets(s.root),
              childProps: { background: 'white.06' }
            },
            // Empty state when no tickets are in progress
            Empty: {
              hide: (el, s) => _workingTickets(s.root).length > 0,
              padding: 'B',
              align: 'center center',
              round: 'A',
              borderWidth: '1px',
              borderStyle: 'dashed',
              borderColor: 'blue',
              color: 'caption',
              background: 'blue.04',
              fontSize: 'Z2',
              text: 'Drag a ticket here to start working'
            }
          }
        },

        Requested: {
          extends: 'IconSection',
          hide: (el, s) => !_requestedTickets(s.root).length,
          icon: 'bell',
          intro: (s) => {
            const n = _requestedTickets(s.root).length
            return [
              { text: 'Others ', color: 'caption' },
              { text: 'asked you', color: 'orange', weight: '700' },
              { text: ` · ${n} ticket${n === 1 ? '' : 's'}`, color: 'caption' }
            ]
          },
          Right: {
            Body: {
              extends: 'TicketCardGrid',
              childExtends: 'TicketCardRequested',
              children: (el, s) => _requestedTickets(s.root)
            }
          }
        }
      },

      // ── Toggleable queue ──
      Queue: {
        flow: 'y',
        gap: 'Z1',
        // Drop target — accept dragged tickets from Working on
        onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' },
        onDragEnter: (e, el) => {
          if (!el.node) return
          el.node.style.outline = '2px solid var(--color-blue)'
          el.node.style.background = 'rgba(0, 133, 255, 0.06)'
          el.node.style.borderRadius = 'var(--spacing-A)'
        },
        onDragLeave: (e, el) => {
          if (!el.node) return
          if (el.node.contains(e.relatedTarget)) return
          el.node.style.outline = ''
          el.node.style.background = ''
          el.node.style.borderRadius = ''
        },
        onDrop: (e, el, s) => {
          e.preventDefault()
          if (el.node) { el.node.style.outline = ''; el.node.style.background = ''; el.node.style.borderRadius = '' }
          const ticketId = parseInt(e.dataTransfer.getData('text/plain'), 10)
          if (!ticketId) return
          el.call('moveTicket', { ticketId, columnKey: 'backlog', position: 0 })
          s.root.update({
            _dragTicketId: null,
            myViewQueueOpen: true,
            myTickets: (s.root.myTickets || []).map(t =>
              t.id === ticketId ? { ...t, column_key: 'backlog' } : t
            )
          })
        },

        QueueHeader: {
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          cursor: 'pointer',
          padding: 'Z 0',

          Caret: {
            extends: 'Icon',
            icon: 'arrowAngleDown',
            boxSize: 'A',
            color: 'caption',
            transition: 'transform 120ms ease',
            transform: (el, s) => s.root?.myViewQueueOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
          },

          CaptionTitle: {
            fontSize: 'Y2',
            Text: {
              text: (el, s) => {
                const n = _backlogTickets(s.root).length
                return `UP NEXT · ${n} ticket${n === 1 ? '' : 's'}`
              }
            }
          },

          onClick: (e, el, s) => {
            s.root.update({ myViewQueueOpen: !s.root.myViewQueueOpen })
          }
        },

        QueueBody: {
          extends: 'TicketCardGrid',
          show: (el, s) => !!s.root?.myViewQueueOpen,
          children: (el, s) => _backlogTickets(s.root)
        }
      },

      Backlog: {
        flow: 'y',
        gap: 'Z1',

        BacklogHeader: {
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          cursor: 'pointer',
          padding: 'Z 0',

          Caret: {
            extends: 'Icon',
            icon: 'arrowAngleDown',
            boxSize: 'A',
            color: 'caption',
            transition: 'transform 120ms ease',
            transform: (el, s) => s.root?.myViewBacklogOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
          },

          CaptionTitle: {
            fontSize: 'Y2',
            Text: {
              text: (el, s) => {
                const n = _backlogTickets(s.root).length
                return `BACKLOG · ${n} ticket${n === 1 ? '' : 's'}`
              }
            }
          },

          onClick: (e, el, s) => {
            s.root.update({ myViewBacklogOpen: !s.root.myViewBacklogOpen })
          }
        },

        BacklogBody: {
          extends: 'TicketCardGrid',
          show: (el, s) => !!s.root?.myViewBacklogOpen,
          children: (el, s) => _backlogTickets(s.root)
        }
      },

      // ── Daily update ──
      TicketsDailyUpdate: {},

      // ── Activity heatmap ──
      TicketsActivityHeatmap: {}
    }
  }
}

// ── Ticket-specific card grid + card ──

export const TicketCardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'A',
  '@tabletS': { gridTemplateColumns: 'repeat(2, 1fr)' },
  '@mobileL': { gridTemplateColumns: '1fr' },
  childExtends: 'TicketCard',
  childrenAs: 'state'
}

const _priorityColor = (p) =>
  ({ critical: 'red', high: 'orange', medium: 'blue', low: 'caption' })[p] ||
  'caption'

// Column color overrides — user-preferred colors for key statuses
const _COLUMN_COLORS = {
  '4d6ab783': 'blue',       // In Progress → blue
  '821cd85d': 'yellow',     // In Review → yellow
  '20f135e3': 'cyan',       // Ready For QA → cyan
  'd677f7ee': 'orange',     // In QA → orange
  '2ea8dd8b': 'red',        // Blocked → red
  'eede5560': 'green'       // Done → green
}
const _colColor = (el, s) => {
  const override = _COLUMN_COLORS[s.column_key]
  if (override) return override
  const col = (el.state?.root?.columns || []).find(c => c.key === s.column_key)
  return el.call('githubColorTokenFromName', col?.color || 'GRAY')
}

export const TicketCard = {
  flow: 'y',
  round: 'A',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'hidden',
  cursor: 'grab',
  draggable: 'true',
  onDragStart: (e, el, s) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(s.id))
    if (s.root) s.root.update({ _dragTicketId: s.id })
  },
  onDragEnd: (e, el, s) => {
    if (s.root) s.root.update({ _dragTicketId: null })
  },
  transition:
    'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px -8px var(--color-shadow)'
  },
  attr: { 'data-ticket-id': (el, s) => String(s.id || '') },
  onClick: (e, el, s) => {
    if (s.root) s.root.update({ selectedTicket: s })
  },

  Content: {
    padding: 'A',
    flow: 'y',
    gap: 'Z',
    position: 'relative',
    flex: '1',

    Top: {
      flow: 'x',
      align: 'flex-start space-between',
      gap: 'Z',

      Title: {
        tag: 'h3',
        margin: '0',
        fontSize: 'A',
        fontWeight: '700',
        lineHeight: '1.3',
        color: 'title',
        flex: '1',
        text: (el, s) => `#${s.number || s.id} · ${s.title || ''}`
      },

      Priority: {
        hide: (el, s) => !s.priority,
        width: 'Y',
        height: 'Y',
        round: '100%',
        flexShrink: '0',
        marginTop: 'Z',
        background: (el, s) => _priorityColor(s.priority),
        title: (el, s) => s.priority || ''
      }
    },

    Meta: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',
      wrap: 'wrap',

      Column: {
        tag: 'span',
        fontSize: 'Y1',
        fontWeight: '700',
        padding: 'V Z',
        round: 'C',
        letterSpacing: '.03em',
        background: (el, s) => {
          const color = _colColor(el, s)
          return `color-mix(in srgb, var(--color-${color}) 20%, transparent)`
        },
        color: (el, s) => `var(--color-${_colColor(el, s)})`,
        text: (el, s) => {
          const col = (el.state?.root?.columns || []).find(
            (c) => c.key === s.column_key
          )
          return col?.label || ''
        }
      },

      Repo: {
        hide: (el, s) => !s.repository,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => s.repository || ''
      },

      Epic: {
        hide: (el, s) => !s.epic_name,
        tag: 'span',
        fontSize: 'Y2',
        color: 'blue',
        fontWeight: '600',
        text: (el, s) => s.epic_name || ''
      },

      IssueType: {
        hide: (el, s) => !s.issue_type,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => s.issue_type || ''
      }
    },

    // Actions removed from Content — moved to card bottom edge
    Actions: null,

    Footer: {
      hide: (el, s) => !s.assignee_email && !s.updated_at,
      flow: 'x',
      align: 'center space-between',
      marginTop: 'V',

      People: {
        flow: 'x',
        gap: 'Z',
        align: 'center left',
        flex: '1',

        Assignee: {
          hide: (el, s) => !s.assignee_email,
          flow: 'x',
          align: 'center left',
          gap: 'V',
          Avatar: {
            tag: 'img',
            width: 'Z1',
            height: 'Z1',
            round: '100%',
            src: (el, s) => {
              const person = el.call('findPerson', s.assignee_email)
              return el.call('avatarUrl', person?.name || s.assignee_email)
            }
          },
          Name: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            text: (el, s) => {
              const person = el.call('findPerson', s.assignee_email)
              return (person?.name || s.assignee_email || '').split(' ')[0]
            }
          }
        },

        RequesterSep: {
          hide: (el, s) => !s.reporter_email || s.reporter_email === s.assignee_email,
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          opacity: '.4',
          text: '←'
        },

        Requester: {
          hide: (el, s) => !s.reporter_email || s.reporter_email === s.assignee_email,
          flow: 'x',
          align: 'center left',
          gap: 'V',
          Avatar: {
            tag: 'img',
            width: 'Z1',
            height: 'Z1',
            round: '100%',
            src: (el, s) => {
              const person = el.call('findPerson', s.reporter_email)
              return el.call('avatarUrl', person?.name || s.reporter_email)
            }
          },
          Name: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            text: (el, s) => {
              const person = el.call('findPerson', s.reporter_email)
              return (person?.name || s.reporter_email || '').split(' ')[0]
            }
          }
        }
      },

      Updated: {
        hide: (el, s) => !s.updated_at,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => (s.updated_at ? s.updated_at.slice(0, 10) : '')
      }
    }
  },

  // Action bar — "Ready to Review" + "Later"
  ActionBar: {
    display: (el, s) => isActiveColumn(s.column_key) ? 'flex' : 'none',
    flow: 'x',
    width: '100%',
    marginTop: 'auto',
    borderTop: '1px solid var(--color-line)',

    ReviewBtn: {
      tag: 'button',
      type: 'button',
      flex: '1',
      align: 'center center',
      padding: 'Z1 0',
      fontSize: 'Y2',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      round: '0',
      background: 'yellow.08',
      color: 'yellow',
      text: 'Ready to Review',
      ':hover': { background: 'yellow.15' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('moveTicket', { ticketId: s.id, columnKey: '821cd85d', position: 0 })
        s.root.update({
          myTickets: (s.root.myTickets || []).map(t =>
            t.id === s.id ? { ...t, column_key: '821cd85d' } : t
          )
        })
      }
    },

    LaterBtn: {
      tag: 'button',
      type: 'button',
      flex: '1',
      align: 'center center',
      padding: 'Z1 0',
      fontSize: 'Y2',
      fontWeight: '600',
      border: 'none',
      borderLeft: '1px solid var(--color-line)',
      cursor: 'pointer',
      round: '0',
      background: 'transparent',
      color: 'caption',
      text: 'Later',
      ':hover': { background: 'overlay' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('moveTicket', { ticketId: s.id, columnKey: 'backlog', position: 0 })
        s.root.update({
          myTickets: (s.root.myTickets || []).map(t =>
            t.id === s.id ? { ...t, column_key: 'backlog' } : t
          )
        })
      }
    }
  },

  // Start on — flush bottom edge button
  StartBtn: {
    tag: 'button',
    type: 'button',
    display: (el, s) => isActiveColumn(s.column_key) ? 'none' : 'flex',
    align: 'center center',
    width: '100%',
    padding: 'Z1 0',
    marginTop: 'auto',
    fontSize: 'Y2',
    fontWeight: '600',
    border: 'none',
    borderTop: '1px solid var(--color-line)',
    cursor: 'pointer',
    round: '0',
    background: 'blue.08',
    color: 'blue',
    text: 'Start on',
    ':hover': { background: 'blue.15' },
    transition: 'background 120ms ease',
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('moveTicket', { ticketId: s.id, columnKey: '4d6ab783', position: 0 })
      const root = s.root
      root.update({
        myTickets: (root.myTickets || []).map(t =>
          t.id === s.id ? { ...t, column_key: '4d6ab783' } : t
        )
      })
    }
  }
}

// ── Awaiting-approval list row — compact single-line decision item ──
//
// Each row shows: title (truncated), age, priority dot, and a direct
// deep-link to /tickets/canvas?ticket=N so Nika can open it in one click.

export const AwaitingApprovalRow = {
  tag: 'a',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Z1 B',
  round: 'A',
  background: 'yellow.04',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'yellow.2',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'background 120ms ease, border-color 120ms ease',
  ':hover': {
    background: 'yellow.1',
    borderColor: 'yellow.4'
  },
  href: (el, s) => `/tickets/canvas?ticket=${s.id || ''}`,

  // Pulsing amber left accent bar
  // animationName is split across two @media props (canonical pattern from GeneralMain.js:1434-1435)
  // to avoid the CSS source-order bug where a flat prop class wins over the compound @media class.
  // The @keyframes injection above registers both askUserPulse (full) and askUserPulseFade (reduced).
  Accent: {
    width: 'V',
    alignSelf: 'stretch',
    flexShrink: '0',
    round: 'V',
    background: 'yellow',
    animationDuration: '2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationFillMode: 'none',
    '@media (prefers-reduced-motion: no-preference)': { animationName: 'askUserPulse' },
    '@media (prefers-reduced-motion: reduce)': { animationName: 'askUserPulseFade' }
  },

  // Priority dot
  PriorityDot: {
    hide: (el, s) => !s.priority,
    width: 'Z',
    height: 'Z',
    round: '100%',
    flexShrink: '0',
    background: (el, s) => _rowPriorityColor(s.priority),
    title: (el, s) => s.priority || ''
  },

  // Ticket number + title
  TitleText: {
    flex: '1',
    fontSize: 'A',
    fontWeight: '600',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => `#${s.number || s.id} · ${s.title || ''}`
  },

  // Age timestamp
  Age: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'yellow',
    fontWeight: '500',
    flexShrink: '0',
    text: (el, s) => _relativeAge(s.updated_at || s.created_at)
  },

  // Open icon
  Arrow: {
    extends: 'Icon',
    icon: 'arrowAngleRight',
    boxSize: 'Z1',
    color: 'yellow',
    flexShrink: '0'
  }
}

// ── Requested ticket card — same as TicketCard but with Claim button ──

export const TicketCardRequested = {
  extends: 'TicketCard',

  Content: {
    Actions: {
      display: 'flex',

      ClaimBtn: {
        tag: 'button',
        type: 'button',
        fontSize: 'Y2',
        fontWeight: '600',
        padding: 'V Z',
        round: 'V',
        border: 'none',
        cursor: 'pointer',
        background: 'blue.15',
        color: 'blue',
        text: 'Claim · Start working',
        ':hover': { background: 'blue.25' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          // Move to In Progress column
          el.call('moveTicket', { ticketId: s.id, columnKey: '4d6ab783', position: 0 })
          const root = s.root
          root.update({
            myTickets: (root.myTickets || []).map(t =>
              t.id === s.id ? { ...t, column_key: '4d6ab783' } : t
            )
          })
        }
      },

      DoneBtn: null,
      ReviewBtn: null
    }
  }
}
